import React, { useEffect, useState } from "react";
import { Alert, Button, LayersThree01, TextInput } from "@opendate-app/setlist";
import { useSeriesShowContext } from "../SeriesShowApp";
import { toastError, toastSuccess } from "../../../shared/toastHelper";

const asInput = (value) => (value === null || value === undefined ? "" : String(value));
const asLimit = (value) => (value === "" ? null : Number(value));

const PassHolderLimitCard = () => {
  const { series, handleUpdatePassHolderLimits } = useSeriesShowContext();
  const limits = series.pass_holder_limits;
  const showTypes = limits?.show_types || [];
  const initialValues = () => ({
    eachDate: asInput(limits?.each_date),
    byShowType: Object.fromEntries(showTypes.map((row) => [row.show_type, asInput(row.limit)])),
  });
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState(null);
  const [saving, setSaving] = useState(false);

  const limitsKey = JSON.stringify(limits || null);
  useEffect(() => {
    setValues(initialValues());
  }, [limitsKey]);

  // series_memberships is only sent while collections are on, so the note stays
  // dark with them.
  if (series.grouping) {
    if (series.series_memberships === undefined) return null;
    return (
      <div className="mt-4" data-testid="pass-holder-limit-collection-note">
        <Alert
          color="gray"
          icon={LayersThree01}
          title="Pass holder limits come from each series"
          description="A collection pass uses the per-date limit of the series each date belongs to, or of the event itself for a single event. Every collection pass on that date shares it."
        />
      </div>
    );
  }

  if (!limits) return null;

  const save = async () => {
    setSaving(true);
    setErrors(null);
    try {
      const response = await handleUpdatePassHolderLimits({
        pass_holder_limit: showTypes.length > 0 ? null : asLimit(values.eachDate),
        show_type_limits: Object.fromEntries(
          Object.entries(values.byShowType).map(([showType, value]) => [showType, asLimit(value)]),
        ),
      });
      const partial = response?.data?.errors || [];
      if (partial.length > 0) {
        setErrors({ title: "Saved, but some dates kept their old limit", messages: partial });
      } else {
        toastSuccess("Pass holder limits saved.");
      }
    } catch (e) {
      const apiErrors = e?.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        setErrors({ title: "Not saved", messages: apiErrors });
      } else {
        toastError("Could not save the pass holder limits. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const limitInput = (key, label, value, onChange) => (
    <div key={key} style={{ width: 180 }}>
      <TextInput
        size="sm"
        type="number"
        inputMode="numeric"
        min={0}
        step="1"
        onWheel={(e) => e.currentTarget.blur()}
        id={`pass-holder-limit-${key}`}
        data-testid={`pass-holder-limit-${key}`}
        label={label}
        placeholder="No limit"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );

  return (
    <div className="series-schedule-card mt-4" data-testid="pass-holder-limit-card">
      <div className="series-schedule-header d-flex align-items-center justify-content-between">
        <div>
          <h4 className="series-schedule-title mb-0">Pass holders per date</h4>
          <div className="text-muted small">
            Tickets held on each date for pass and pack holders. One limit per date, shared by every collection pass that covers it.
          </div>
        </div>
        <Button variant="secondary" data-testid="save-pass-holder-limits" disabled={saving} onClick={save}>
          Save
        </Button>
      </div>
      <div className="series-detail-card-body">
        {errors && (
          <div className="mb-3" data-testid="pass-holder-limit-errors">
            <Alert color="error" role="alert" title={errors.title} description={errors.messages.join(" ")} />
          </div>
        )}
        <div className="d-flex flex-wrap" style={{ gap: 16 }}>
          {showTypes.length > 0
            ? showTypes.map((row) => limitInput(
              row.show_type,
              `${row.show_type} dates`,
              values.byShowType[row.show_type] ?? "",
              (value) => setValues((current) => ({ ...current, byShowType: { ...current.byShowType, [row.show_type]: value } })),
            ))
            : limitInput("each-date", "Each date", values.eachDate, (value) => setValues((current) => ({ ...current, eachDate: value })))}
        </div>
      </div>
    </div>
  );
};

export default PassHolderLimitCard;
